<template>
	<v-dialog v-model="dialog" persistent max-width="1300px">
		<v-card>
			<v-form>
				<v-toolbar flat dense cad>
					<v-toolbar-title class="subheading">{{ title }}</v-toolbar-title>
					<v-spacer></v-spacer>
					<div v-if="goal.createdByEmployeeId != null">
						<table>
							<tr>
								<td style="text-align: right;">
									<div class="created-by">Created By {{ createdByEmployeeName }}</div>
									<div class="created-by">
										<AppDate :date="createdDate" />
									</div>
								</td>
								<td style="width:1px;">
									<v-avatar size="30px" class="created-by-avatar">
										<img :src="getAvatar(goal.createdByEmployeeId)" class="avatar-img" />
									</v-avatar>
								</td>
							</tr>
						</table>
					</div>
				</v-toolbar>

				<v-divider></v-divider>
				<v-card-text>
					<v-alert v-if="!canEdit" class="text-xs-left mb-3" :value="true" color="warning" icon="lock" outline>This goal was created by {{ createdByEmployeeName }}, therefore not all fields are allowed to be updated.</v-alert>

					<v-layout row wrap>
						<v-flex md5>
							<v-textarea v-model="form.goal" :error-messages="goalErrors" :readonly="!canEdit" auto-grow box rows="1" label="Goal" required @input="$v.form.goal.$touch()" @blur="$v.form.goal.$touch()"></v-textarea>

							<v-textarea v-model="form.description" :readonly="!canEdit" auto-grow box rows="3" label="Description (optional)"></v-textarea>

							<v-menu ref="dueDateMenu" v-model="form.dueDateUTC.menu" :disabled="!canEdit" :close-on-content-click="false" :nudge-right="40" :return-value.sync="form.dueDateUTC.date" lazy transition="scale-transition" offset-y full-width min-width="290px">
								<v-text-field slot="activator" v-model="dueDateFormatted" label="Due Date" readonly></v-text-field>
								<v-date-picker v-model="form.dueDateUTC.date" @input="$refs.dueDateMenu.save(form.dueDateUTC.date)"></v-date-picker>
							</v-menu>

							<div>
								<v-radio-group v-model="form.category" :mandatory="true" :readonly="!canEdit">
									<v-radio label="Business Goal" :value="2"></v-radio>
									<v-radio label="Professional Development Goal" :value="0"></v-radio>
									<v-radio label="Personal Goal" :value="1"></v-radio>
								</v-radio-group>

								<v-switch v-show="allowCompleteOnCreate" v-model="form.complete" label="Goal Complete"></v-switch>
							</div>
						</v-flex>

						<v-flex md5 class="pl-4 pr-4">
							<v-card class="ma-0">
								<v-toolbar flat dense cad dark color="primary">
									<v-toolbar-title class="subheading">Goal KPI's</v-toolbar-title>
									<v-spacer></v-spacer>
									<v-tooltip bottom>
										<template v-slot:activator="{ on }">
											<v-icon medium v-on="on">mdi-information-variant</v-icon>
										</template>
										<span>KPI's are a great way to break your goals down into smaller actionable steps</span>
									</v-tooltip>
								</v-toolbar>

								<v-divider></v-divider>
								<v-card-text class="pa-0">
									<employee-goal-kpi-list :kpis="form.kpis" :can-edit="canEdit" @onEditKpi="onEditKpi" @onAddKpi="onAddKpi" @onDeleteKpi="deleteKpi" />
								</v-card-text>
							</v-card>
						</v-flex>

						<v-flex md2>
							<v-card class="ma-0">
								<v-toolbar flat dense cad dark color="pink">
									<v-toolbar-title class="subheading">
										<v-icon>mdi-bell-ring-outline</v-icon> Reminders </v-toolbar-title>
									<v-spacer></v-spacer>
									<v-tooltip bottom>
										<template v-slot:activator="{ on }">
											<v-icon medium v-on="on">mdi-information-variant</v-icon>
										</template>
										<span>Reminders are only sent out if the goal is incomplete</span>
									</v-tooltip>
								</v-toolbar>

								<v-divider></v-divider>
								<v-card-text class="pa-0">
									<reminder-list :reminders="form.reminders" @onEditReminder="onEditReminder" @onAddReminder="onAddReminder" @onDeleteReminder="deleteReminder" />
								</v-card-text>
							</v-card>
						</v-flex>
					</v-layout>
				</v-card-text>
				<v-card-actions>
					<v-spacer></v-spacer>

					<v-btn flat @click="onCancelClick">Cancel</v-btn>
					<v-btn v-if="this.form.id != undefined && canEdit" color="error" flat @click="deleteGoal" :loading="isDeleting" :disabled="isDeleting || isSaving">Delete Goal <span slot="loader">Deleting...</span></v-btn>
					<v-btn color="primary" flat @click="submit" :loading="isSaving" :disabled="isSaving || isDeleting">Save <span slot="loader">Saving...</span></v-btn>
				</v-card-actions>
			</v-form>
		</v-card>

		<employee-goal-kpi-editor v-if="kpiEditorDialog" :can-edit="canEdit" :kpi="editedKpi" :title="kpiEditorTitle" :dialog="kpiEditorDialog" @closeEditor="kpiEditorDialog = false" @save="saveKpi" @delete="deleteKpi" />

		<reminder-editor v-if="reminderEditorDialog" :reminder="editedReminder" :existing-reminders="form.reminders" :title="reminderEditorTitle" :dialog="reminderEditorDialog" :default-minutes="10080" @closeEditor="reminderEditorDialog = false" @save="saveReminder" @delete="deleteReminder" />

			<v-snackbar v-model="goalDeletingErrorNotif" top>
			Goal could not be deleted at this time, refresh the page and try again.
			<v-btn color="pink" flat @click="goalDeletingErrorNotif = false">Close</v-btn>
		</v-snackbar>

		<v-snackbar v-model="goalSavingErrorNotif" top>
			Goal could not be saved at this time, refresh the page and try again.
			<v-btn color="pink" flat @click="goalSavingErrorNotif = false">Close</v-btn>
		</v-snackbar>

	</v-dialog>
</template>

<script>
	import { mapGetters } from 'vuex';
	import { validationMixin } from 'vuelidate';
	import avatarMixin from '@/mixins/avatarMixin';
	import { required } from 'vuelidate/lib/validators';
	import EmployeeGoalKpiList from '@/components/employee/EmployeeGoalKpiList';
	import EmployeeGoalKpiEditor from '@/components/employee/EmployeeGoalKpiEditor';
	import ReminderList from '@/components/reminders/ReminderList';
	import ReminderEditor from '@/components/reminders/ReminderEditor';

	export default {
		components: {
			EmployeeGoalKpiList,
			EmployeeGoalKpiEditor,
			ReminderList,
			ReminderEditor
		},
		props: {
			goal: {
				type: Object,
				default: () => ({
					id: null,
					goal: null,
					description: null,
					milestones: [],
					kpis: [],
					dueDateUTC: null,
					comments: [],
					complete: false,
					reminders: []
				})
			},
			dialog: {},
			title: {
				required: true,
				type: String
			},
			allowCompleteOnCreate: {
				type: Boolean,
				required: false,
				default: true
			},
			category: {
				type: Number
			},
			employeeId: {
				type: String,
				required: true
			}
		},
		created() {
			const goal = JSON.parse(JSON.stringify(this.goal));

			this.form = {
				id: goal.id,
				goal: goal.goal,
				description: goal.description,
				milestones: goal.milestones,
				kpis: goal.kpis !== undefined ? goal.kpis : [],
				dueDateUTC: {
					date: this.$options.filters.formatDate(goal.dueDateUTC, 'YYYY-MM-DD'),
					menu: false
				},
				comments: goal.comments,
				complete: goal.complete,
				category: goal.id !== null ? goal.category : this.category,
				reminders: goal.reminders
			};

			if (goal.id === null) {
				this.form.reminders = this.getDefaultGoalReminders();
			}

			this.originalFormJson = JSON.stringify(this.form);
		},
		data() {
			return {
				form: {
					id: null,
					goal: null,
					description: null,
					milestones: [],
					kpis: [],
					dueDateUTC: null,
					comments: [],
					complete: false,
					reminders: []
				},
				kpiEditorDialog: false,
				editedKpi: undefined,

				originalFormJson: null,

				reminderEditorDialog: false,
				editedReminderIndex: -1,
				editedReminder: undefined,

				isSaving: false,
				isDeleting: false,
				goalSavingErrorNotif: false,
				goalDeletingErrorNotif: false
			};
		},
		computed: {
			...mapGetters({
				loggedInEmployee: 'loggedInEmployee',
				employees: 'employees/employeesMini'
			}),
			goalErrors() {
				const errors = [];
				if (!this.$v.form.goal.$dirty) return errors;
				!this.$v.form.goal.required && errors.push('Goal is required.');
				return errors;
			},
			dueDateFormatted() {
				return this.$options.filters.formatDate(this.form.dueDateUTC.date);
			},
			kpiEditorTitle() {
				return this.editedKpi == null ? 'New KPI' : 'Edit KPI';
			},
			reminderEditorTitle() {
				return this.editedReminderIndex === -1 ? 'New Reminder' : 'Edit Reminder';
			},
			canEdit() {
				if (this.goal.id != null) {
					return this.hasAccessToModify;
				}
				return true;
			},
			hasAccessToModify() {
				return this.$_hasAccessToEmployee(this.loggedInEmployee, this.goal.createdByEmployeeId);
			},
			createdByEmployeeName() {
				if (this.goal.createdByEmployeeId != null) {
					return this.getEmployeeName(this.goal.createdByEmployeeId);
				}

				return '';
			},
			createdDate() {
				return this.goal.createdDateUTC;
			},
			isDirty() {
				return this.originalFormJson !== JSON.stringify(this.form);
			},
			isNew() {
				return this.form.id == undefined;
			}
		},
		mixins: [validationMixin, avatarMixin],

		validations: {
			form: {
				goal: { required }
			}
		},
		methods: {
			submit() {
				this.$v.form.$touch();

				if (this.$v.form.$invalid) {
					console.log('invalid form');
				} else {

					this.isSaving = true;

					let goal = {
						id: !this.isNew ? this.form.id : this.newGuid(),
						goal: this.form.goal,
						description: this.form.description,
						milestones: this.form.milestones,
						kpis: this.form.kpis,
						dueDateUTC: this.form.dueDateUTC.date,
						comments: this.form.comments,
						complete: this.form.complete,
						category: this.form.category,
						reminders: this.form.reminders
					};

					if (this.isNew) {					
						this.$store
							.dispatch('employees/createGoal', {
								employeeId: this.employeeId,
								goal
							})
							.then(response => {
								this.$emit('onSaved', goal);
								this.isSaving = false;
								this.closeEditor();		
							},
							error => {
								this.isSaving = false;
								console.error('Error adding goal');
								this.goalSavingErrorNotif = true;
							});

					} else {
						this.$store
							.dispatch('employees/updateGoal', {
								employeeId: this.employeeId,
								goal
							})
							.then(response => {								
								this.$emit('onSaved', goal);
								this.isSaving = false;
								this.closeEditor();
							},
							error => {
								this.isSaving = false;
								console.error('Error updating goal');
								this.goalSavingErrorNotif = true;
							});
					}
				}
			},
			deleteGoal() {
				if (confirm('Are you sure you want to delete this Goal?')) {

					this.isDeleting = true;
					this.$store
						.dispatch('employees/deleteGoal', {
							employeeId: this.employeeId,
							goalId: this.form.id
						})
						.then(
							response => {
								this.$emit('onDeleted', this.form.id);
								this.isDeleting = false;
								this.closeEditor();			
							},
							error => {
								this.isDeleting = false;
								console.error('Error deleting goal');
								this.goalDeletingErrorNotif = true;
							}
						);
				}
			},
			onCancelClick() {
				if (this.isDirty) {
					if (confirm('You have unsaved changes, do you wish to proceed and lose these unsaved changes?')) {
						this.closeEditor();
					}
				} else {
					this.closeEditor();
				}
			},
			closeEditor() {
				this.clear();
				this.$emit('closeEditor');
			},
			clear() {
				this.$v.form.$reset();
				(this.form.id = null), (this.form.goal = null), (this.form.description = null), (this.form.dueDateUTC.date = '');
				this.form.category = this.category;
			},
			onEditKpi(item) {
				this.editedKpi = Object.assign({}, item);
				this.kpiEditorDialog = true;
			},
			onAddKpi() {
				this.editedKpi = undefined;
				this.kpiEditorDialog = true;
			},
			saveKpi(kpi) {
				if (this.editedKpi != null) {
					const index = this.form.kpis.findIndex(x => x.id === this.editedKpi.id);
					Object.assign(this.form.kpis[index], kpi);
					// this.$emit('onEditGoal', { employeeId: this.employeeId, goal });
				} else {
					this.form.kpis.push(kpi);
					// this.$emit('onAddGoal', { employeeId: this.employeeId, goal });
				}
			},
			deleteKpi(id) {
				this.kpiEditorDialog = false;
				const index = this.form.kpis.indexOf(this.form.kpis.find(x => x.id == id));
				if (index > -1) {
					this.form.kpis.splice(index, 1);
				}
			},
			onEditReminder(item) {
				this.editedReminderIndex = this.form.reminders.indexOf(item);
				this.editedReminder = Object.assign({}, item);
				this.reminderEditorDialog = true;
			},
			onAddReminder() {
				this.editedReminderIndex = -1;
				this.editedReminder = undefined;
				this.reminderEditorDialog = true;
			},
			saveReminder(reminder) {
				if (this.editedReminderIndex > -1) {
					Object.assign(this.form.reminders[this.editedReminderIndex], reminder);
				} else {
					this.form.reminders.push(reminder);
				}
			},
			deleteReminder(id) {
				this.reminderEditorDialog = false;
				const index = this.form.reminders.indexOf(this.form.reminders.find(x => x.id == id));
				if (index > -1) {
					this.form.reminders.splice(index, 1);
				}
			},
			getEmployeeName(employeeId) {
				if (this.employees == null) {
					return '';
				}
				let employee = this.employees.find(x => x.id === employeeId);
				if (employee !== null) {
					return employee.fullName;
				}

				return '';
			},
			getDefaultGoalReminders() {
				return [
					{
						id: this.newGuid(),
						deliveryMethods: [0],
						minutesBefore: 10080
					}
				];
			}
		}
	};
</script>

<style scoped>
	.v-input__slot {
		align-items: right;
		justify-content: right;
	}
	.created-by {
		font-size: 12px;
		color: rgba(0, 0, 0, 0.54);
		line-height: 1.2em;
	}
	.created-by-avatar {
		margin-left: 10px;
	}
</style>
